import { RadioGroup, NativeSelect, Stack, Text, Field, type RadioGroupValueChangeDetails } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { useGeoUnits } from '../../hooks/useGeoAnalytics'
import { explainCopy } from '../../lib/explainCopy'
import type { ContestRequest, ContestType } from '../../types/electionSim'
import { US_STATES } from '../../types/geoAnalytics'
import type { GeoUnitTypeKey } from '../../types/geoAnalytics'

interface OfficeOption {
  office: string
  label: string
}

/** District office -> ElectionOffice enum name + the geo-unit type useGeoUnits fetches for it. */
const DISTRICT_OFFICES: (OfficeOption & { unitType: GeoUnitTypeKey })[] = [
  { office: 'StateHouse', label: 'State House', unitType: 'stateHouseDistrict' },
  { office: 'StateSenate', label: 'State Senate', unitType: 'stateSenateDistrict' },
  { office: 'USHouse', label: 'US House', unitType: 'congressionalDistrict' },
]

const STATEWIDE_OFFICES: OfficeOption[] = [
  { office: 'Governor', label: 'Governor' },
  { office: 'USSenate', label: 'US Senate' },
  { office: 'President', label: 'President' },
]

/** Chamber -> the matching ElectionOffice enum name the backend requires alongside it (see
 * ElectionSimValidation.ValidateContest / SimulationService.ResolveContestUnitsAsync, which parses
 * contest.Office for every contest type, including chamber). */
const CHAMBER_OPTIONS: { chamber: 'stateHouse' | 'stateSenate'; office: string; label: string }[] = [
  { chamber: 'stateHouse', office: 'StateHouse', label: 'State House' },
  { chamber: 'stateSenate', office: 'StateSenate', label: 'State Senate' },
]

export interface ContestPickerProps {
  value: ContestRequest
  onChange: (contest: ContestRequest) => void
}

/**
 * Race picker for the what-if simulator: contest type (district/statewide/chamber), state, then
 * mode-specific controls. Built responsively (Stack, no fixed desktop-only widths) per the mobile
 * constraint, even though it is only mounted on the ScenarioWorkspacePage desktop branch.
 */
export function ContestPicker({ value, onChange }: ContestPickerProps) {
  const districtUnitType =
    DISTRICT_OFFICES.find((o) => o.office === value.office)?.unitType ?? DISTRICT_OFFICES[0].unitType

  const geoUnitsQuery = useGeoUnits(
    value.type === 'district' && value.state ? { type: districtUnitType, state: value.state } : null
  )
  const districtOptions = [...(geoUnitsQuery.data?.features ?? [])]
    .map((feature) => ({
      id: String(feature.properties?.id ?? ''),
      name: String(feature.properties?.name ?? ''),
    }))
    .filter((option) => option.id)
    .sort((a, b) => a.name.localeCompare(b.name))

  function handleTypeChange(next: string) {
    const type = next as ContestType
    if (type === 'district') {
      const office = DISTRICT_OFFICES.some((o) => o.office === value.office)
        ? value.office
        : DISTRICT_OFFICES[0].office
      onChange({ ...value, type, office, chamber: null })
    } else if (type === 'statewide') {
      const office = STATEWIDE_OFFICES.some((o) => o.office === value.office)
        ? value.office
        : STATEWIDE_OFFICES[0].office
      onChange({ ...value, type, office, districtGeoUnitId: null, chamber: null })
    } else {
      const chamberOption = CHAMBER_OPTIONS.find((c) => c.chamber === value.chamber) ?? CHAMBER_OPTIONS[0]
      onChange({ ...value, type, chamber: chamberOption.chamber, office: chamberOption.office, districtGeoUnitId: null })
    }
  }

  function handleRaceKindChange(next: string) {
    const raceKind = next as 'general' | 'primary'
    // Chamber forecasts are unavailable for primaries — drop back to a district contest,
    // preserving the chamber's office when it's already a valid district office (e.g. State Senate).
    if (raceKind === 'primary' && value.type === 'chamber') {
      const office = DISTRICT_OFFICES.some((o) => o.office === value.office)
        ? value.office
        : DISTRICT_OFFICES[0].office
      onChange({ ...value, raceKind, type: 'district', office, chamber: null, districtGeoUnitId: null })
      return
    }
    onChange({ ...value, raceKind })
  }

  function handleStateChange(state: string) {
    onChange({ ...value, state, districtGeoUnitId: null })
  }

  function handleDistrictOfficeChange(office: string) {
    onChange({ ...value, office, districtGeoUnitId: null })
  }

  function handleDistrictChange(districtGeoUnitId: string) {
    onChange({ ...value, districtGeoUnitId: districtGeoUnitId || null })
  }

  function handleStatewideOfficeChange(office: string) {
    onChange({ ...value, office })
  }

  function handleChamberChange(chamber: string) {
    const chamberOption = CHAMBER_OPTIONS.find((c) => c.chamber === chamber) ?? CHAMBER_OPTIONS[0]
    onChange({ ...value, chamber: chamberOption.chamber, office: chamberOption.office })
  }

  return (
    <Stack gap={4}>
      <Field.Root>
        <Field.Label fontSize="sm" fontWeight="semibold">
          <Tooltip content={explainCopy.raceKindToggle()} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
            placement: "top"
          }}>
            <Text as="span" tabIndex={0} cursor="help" borderBottomWidth="1px" borderBottomStyle="dashed" borderBottomColor="accent">
              Race kind
            </Text>
          </Tooltip>
        </Field.Label>
        <RadioGroup.Root value={value.raceKind ?? 'general'} onValueChange={(details: RadioGroupValueChangeDetails) => handleRaceKindChange(details.value ?? 'general')}>
          <Stack direction={{ base: 'column', sm: 'row' }} gap={4}>
            <RadioGroup.Item value="general"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>General</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="primary"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Primary</RadioGroup.ItemText></RadioGroup.Item>
          </Stack>
        </RadioGroup.Root>
      </Field.Root>

      <Field.Root>
        <Field.Label fontSize="sm" fontWeight="semibold">
          Contest type
        </Field.Label>
        <RadioGroup.Root value={value.type} onValueChange={(details: RadioGroupValueChangeDetails) => handleTypeChange(details.value ?? value.type)}>
          <Stack direction={{ base: 'column', sm: 'row' }} gap={4}>
            <RadioGroup.Item value="district"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>District</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="statewide"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Statewide</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="chamber" disabled={value.raceKind === 'primary'}><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Chamber control</RadioGroup.ItemText></RadioGroup.Item>
          </Stack>
        </RadioGroup.Root>
      </Field.Root>

      <Field.Root>
        <Field.Label fontSize="sm" fontWeight="semibold">
          State
        </Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            value={value.state}
            onChange={(e) => handleStateChange(e.target.value)}
            placeholder="Select a state">
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      {value.type === 'district' && (
        <>
          <Field.Root>
            <Field.Label fontSize="sm" fontWeight="semibold">
              Office
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={value.office}
                onChange={(e) => handleDistrictOfficeChange(e.target.value)}>
                {DISTRICT_OFFICES.map((o) => (
                  <option key={o.office} value={o.office}>
                    {o.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="sm" fontWeight="semibold">
              District
            </Field.Label>
            <NativeSelect.Root disabled={!value.state || geoUnitsQuery.isLoading}>
              <NativeSelect.Field
                value={value.districtGeoUnitId ?? ''}
                onChange={(e) => handleDistrictChange(e.target.value)}
                placeholder={geoUnitsQuery.isLoading ? 'Loading districts…' : 'Select a district'}>
                {districtOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        </>
      )}

      {value.type === 'statewide' && (
        <Field.Root>
          <Field.Label fontSize="sm" fontWeight="semibold">
            Office
          </Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={value.office}
              onChange={(e) => handleStatewideOfficeChange(e.target.value)}>
              {STATEWIDE_OFFICES.map((o) => (
                <option key={o.office} value={o.office}>
                  {o.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
      )}

      {value.type === 'chamber' && (
        <Field.Root>
          <Field.Label fontSize="sm" fontWeight="semibold">
            Chamber
          </Field.Label>
          <RadioGroup.Root value={value.chamber ?? 'stateHouse'} onValueChange={(details: RadioGroupValueChangeDetails) => handleChamberChange(details.value ?? 'stateHouse')}>
            <Stack direction={{ base: 'column', sm: 'row' }} gap={4}>
              {CHAMBER_OPTIONS.map((c) => (
                <RadioGroup.Item key={c.chamber} value={c.chamber}><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>
                  {c.label}
                </RadioGroup.ItemText></RadioGroup.Item>
              ))}
            </Stack>
          </RadioGroup.Root>
        </Field.Root>
      )}
    </Stack>
  );
}
